import { FC } from "react";
import { Dialog, DialogContent, Box, Typography, IconButton, useMediaQuery } from "@mui/material";
import theme from "@/theme";
import { styles } from "./stylesPremiumSubscriptionModal";
import FeatureItem from "./components/FeatureItem";
import {
  useGetPremiumSubscriptionPriceQuery,
  useOneTimePurchaseMutation,
} from "@/store/api/premiumApi";
import PremiumCheckmark from "@/assets/svg/PremiumCheckmark";
import StarSvg from "@/assets/svg/StarSvg";
import CloseSvg from "@/assets/svg/CloseSvg";

interface PremiumSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  setToast: React.Dispatch<React.SetStateAction<{ message: string; success?: boolean } | null>>;
}

const PremiumSubscriptionModal: FC<PremiumSubscriptionModalProps> = ({
  open,
  onClose,
  setToast,
}) => {
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const [oneTimePurchase] = useOneTimePurchaseMutation();
  const { data, isLoading, isError } = useGetPremiumSubscriptionPriceQuery();
  const handlePremium = async () => {
    setToast({ message: "Loading..." });
    try {
      await oneTimePurchase({}).unwrap();
      setToast({ message: "Purchase successful!", success: true });
      onClose();
    } catch (e: any) {
      const msg = e?.data?.message || e?.message || "Purchase failed!";
      setToast({ message: msg, success: false });
      console.error("Purchase failed:", e);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(4px)",
            background: "rgba(0,0,0,0.3)",
          },
        },
        paper: {
          sx: styles.dialogPaper(isLgUp),
        },
      }}
    >
      <IconButton onClick={onClose} sx={styles.closeButton}>
        <CloseSvg size={22} color="#000000" />
      </IconButton>

      <DialogContent sx={styles.dialogContent}>
        {/* Header */}
        <Box sx={styles.headerWrapper}>
          <Box sx={styles.headerTitleRow}>
            <Typography sx={styles.title}>Premium Subscription</Typography>
            <Box sx={{ width: "24px", height: "24px" }}>
              <PremiumCheckmark size={24} />
            </Box>
          </Box>

          <Typography sx={styles.subtitle}>
            Get access to exclusive features and improve your experience on the platform
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={styles.contentWrapper(isLgUp)}>
          <Box sx={styles.topRow}>
            {/* BASIC */}
            <Box sx={styles.basicCard}>
              <Box sx={styles.planHeader}>
                <Typography sx={styles.basicTitle}>Basic</Typography>
                <Typography sx={styles.basicSubtitle}>Free features</Typography>
              </Box>

              <Box sx={styles.featuresPlaceholder}>
                <FeatureItem label="No advertisements" iconBgColor="#DCFCE7" iconColor="#00A63E" />

                <FeatureItem
                  label="Earn money from other users for likes and zaps"
                  iconBgColor="#DCFCE7"
                  iconColor="#00A63E"
                />

                <FeatureItem
                  label="Basic post feed sorting"
                  iconBgColor="#DCFCE7"
                  iconColor="#00A63E"
                />

                <FeatureItem
                  label="3 stocks in trending function"
                  iconBgColor="#DCFCE7"
                  iconColor="#00A63E"
                />

                <FeatureItem
                  label="Can't comment on a post until you have liked it"
                  iconType="close"
                  iconBgColor="#FFDBE2"
                  iconColor="#CE212B"
                  textColor="#9DA6B0"
                />
              </Box>
            </Box>

            {/* PREMIUM */}
            <Box sx={styles.premiumCard}>
              <Box sx={styles.planHeader}>
                <Box sx={styles.planTitleRow}>
                  <Typography sx={styles.premiumTitle}>Premium</Typography>

                  <Box sx={styles.popularBadge}>
                    <Typography sx={styles.popularText}>Popular</Typography>
                  </Box>
                </Box>

                <Typography sx={styles.premiumSubtitle}>All basic features +</Typography>
              </Box>

              <Box sx={styles.featuresPlaceholder}>
                <FeatureItem
                  label="Blue tick on your user name"
                  iconBgColor="#FFFFFF33"
                  iconColor="#FFFFFF"
                  textColor="#FFFFFF"
                />

                <FeatureItem
                  label="Comment on any user's post"
                  iconBgColor="#FFFFFF33"
                  iconColor="#FFFFFF"
                  textColor="#FFFFFF"
                />

                <FeatureItem
                  label="See 10 results in trending instead of 3"
                  iconBgColor="#FFFFFF33"
                  iconColor="#FFFFFF"
                  textColor="#FFFFFF"
                />

                <FeatureItem
                  label="More recent time periods for sorting your main feed"
                  iconBgColor="#FFFFFF33"
                  iconColor="#FFFFFF"
                  textColor="#FFFFFF"
                />

                <FeatureItem
                  label="More recent time periods for sorting trending functions"
                  iconBgColor="#FFFFFF33"
                  iconColor="#FFFFFF"
                  textColor="#FFFFFF"
                />

                <FeatureItem
                  label="Posts sorting function exclusively from users you follow"
                  iconBgColor="#FFFFFF33"
                  iconColor="#FFFFFF"
                  textColor="#FFFFFF"
                />

                <FeatureItem
                  label="Sort trending functions for post data from the users you follow"
                  iconBgColor="#FFFFFF33"
                  iconColor="#FFFFFF"
                  textColor="#FFFFFF"
                />

                <FeatureItem
                  label="Get notifications from chosen users for their posts and comments"
                  iconBgColor="#FFFFFF33"
                  iconColor="#FFFFFF"
                  textColor="#FFFFFF"
                />

                <FeatureItem
                  label="Change default amount for likes"
                  iconBgColor="#FFFFFF33"
                  iconColor="#FFFFFF"
                  textColor="#FFFFFF"
                />
              </Box>
            </Box>
          </Box>

          {/* Bottom block */}
          <Box sx={styles.bottomBlock}>
            <Box sx={styles.bottomInner}>
              {/* Icon */}
              <Box sx={styles.bottomIcon}>
                <StarSvg size={19} />
              </Box>

              {/* Texts */}
              <Box sx={styles.bottomTexts}>
                <Box sx={styles.bottomTitle}>Ready to get more features?</Box>
                <Box sx={styles.bottomSubtitle}>
                  Upgrade to premium account and unlock all features
                </Box>
              </Box>

              {/* Price + button */}
              <Box sx={styles.bottomRight}>
                <Box sx={styles.priceBlock}>
                  <Box sx={styles.priceRow}>
                    <Box sx={styles.priceValue}>
                      {isLoading ? "" : isError || !data ? "" : data.value}
                    </Box>
                    <Box sx={styles.priceUnit}>USDC</Box>
                  </Box>
                  <Box sx={styles.priceNote}>Per month</Box>
                </Box>

                <Box component="button" sx={styles.buyButton} type="button" onClick={handlePremium}>
                  Buy Premium
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PremiumSubscriptionModal;
